import {makeAutoObservable, reaction, runInAction} from 'mobx'
import Agent from '../api/Agent'
import {ILineItemAttachment, LineItemAttachmentFormValues} from '../models/LineItemAttachment'
import {toast} from 'react-toastify'
import {RootStore} from './RootStore'
import dayjs from 'dayjs'


export default class LineItemAttachmentStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.lineItemAttachmentRegistry.clear()
                this.loadLineItemAttachment()
            }
        )
    }

    lineItemAttachmentRegistry = new Map()
    lineItemAttachment: ILineItemAttachment | null = null
    editMode = false


    currentLineItemId = '';
    lineItemAttachmentCount = 0
    page = 0
    limit = 5

    predicate = new Map()

    setCurrentLineItemId = (lineItemId: string) => {
        this.currentLineItemId = lineItemId;
    }

    setPredicate = (predicate?: string, value?: any) => {
        if (!predicate || predicate === '') {
            this.predicate.clear()
        } else {
            this.predicate.delete(predicate)
            if (!!value)
                this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!!value) params.append(key, value)
        })
        return params
    }

    get totalPages() {
        return Math.ceil(this.lineItemAttachmentCount / this.limit)
    }

    get lineItemAttachmentsById() {
        return Array.from(this.lineItemAttachmentRegistry.values()).sort((a, b) => b.id - a.id)
    }

    get lineItemAttachmentsByDate() {
        return Array.from(this.lineItemAttachmentRegistry.values()).sort(
            (a, b) => dayjs(b.createdOn).isBefore(a.createdOn) ? -1 : 1//   Date.parse(a.SubmittedOn)-Date.parse(b.SubmittedOn)
        )
    }

    getLineItemAttachment = (id: string) => {
        return this.lineItemAttachmentRegistry.get(id)
    }

    setPage = (page: number) => {
        this.page = page
    }

    setLimit = (limit: number) => {
        this.limit = limit
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against 
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.lineItemAttachmentRegistry.clear()
        this.loadLineItemAttachment()
    }

    clearLineItemAttachment = () => {
        this.lineItemAttachmentRegistry.clear()
    }

    loadLineItemAttachment = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            if (!!this.currentLineItemId) {
                const lineItemAttachmentsEnvelope = await Agent.LineItemAttachments.list(this.axiosParams);
                const {modelsDto, modelCount} = lineItemAttachmentsEnvelope;
                runInAction(() => {
                    modelsDto.forEach((lineItemAttachment: ILineItemAttachment) => {
                        this.lineItemAttachmentRegistry.set(lineItemAttachment.id, lineItemAttachment)
                    })
                    this.lineItemAttachmentCount = modelCount
                })
            }
        } catch (error) {
            throw error
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }

    getLineItemAttachmentById = async (id: string) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            let lineItemAttachment = await Agent.LineItemAttachments.byId(id)
            runInAction(() => {
                this.lineItemAttachment = lineItemAttachment
                this.lineItemAttachmentRegistry.set(lineItemAttachment.id, lineItemAttachment)
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            return lineItemAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        }
        runInAction(() => {
            this.rootStore.commonStore.setIsLoading(loadingId)
        })
    }


    createLineItemAttachment = async (lineItemAttachment: LineItemAttachmentFormValues) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()

        try {
            const newAttachment = await Agent.LineItemAttachments.create(lineItemAttachment)
            await this.loadLineItemAttachment()
            runInAction(() => {
                this.lineItemAttachmentRegistry.set(newAttachment.id, newAttachment)
                this.rootStore.modalStore.closeModal('LineItemAttachmentModal')
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
            toast.success('Saved')
            return newAttachment
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            runInAction(() => {
                this.rootStore.commonStore.setIsLoading(loadingId)
            })
        }
    }

}